// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CheckboxStyle } from "@insite/mobius/Checkbox";
import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import IconCheckmark from "@stoneage/XDL/Icons/IconCheckmark";
import { fontConfig } from "@stoneage/XDL/constants";
import { xBaseTheme } from "@stoneage/XDL/types";
import { color } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";

export const CheckboxInputSelect = `span[role="checkbox"]`;

export const CheckboxCheckedSelector = `
    &[aria-checked="true"]:not([aria-disabled="true"]),
    &[aria-checked="mixed"]:not([aria-disabled="true"])
`;

export const CheckboxDefaultSelector = `&[aria-checked="false"]:not([aria-disabled="true"])`;
export const CheckboxDisabledSelector = `&[aria-disabled="true"]`;

const defaultStateStyles = css`
    & ${CheckboxInputSelect} {
        height: 16px;
        min-width: 16px;
        width: 16px;
        border-color: ${color("common.ui7")};
        background: ${color("common.ui3")};
        color: ${color("common.ui3")};
        border-radius: 0;
        &:focus {
            box-shadow: none;
        }
        & + label {
            margin-left: 6px;
        }
    }
`;

const checkedStyle = css`
    & ${CheckboxInputSelect} {
        ${CheckboxCheckedSelector} {
            background: ${color("secondary")};
            border-color: ${color("secondary")};
            color: ${color("common.ui3")};
            justify-content: center;
        }
    }
`;
const hoverStyle = css`
    &:hover {
        ${CheckboxInputSelect} {
            ${CheckboxCheckedSelector} {
                background: ${color("primary")};
                border-color: ${color("primary")};
            }
            ${CheckboxDefaultSelector} {
                border-color: ${color("primary")};
            }
        }
    }
`;

const disabledStyle = css`
    ${CheckboxInputSelect} {
        ${CheckboxDisabledSelector} {
            cursor: not-allowed;
            justify-content: center;
            opacity: 0.33;
            background: ${color("common.ui4")};
            color: ${color("text")};
            border: 1px solid ${color("common.ui5")};
        }
    }
`;

export default {
    defaultProps: {
        ...CheckboxPresentationPropsDefault,
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: fontConfig.xSmallSingleLine.size,
            weight: fontConfig.weight.regular,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                user-select: none;
                &:empty {
                    display: none;
                }
            `,
        },
        iconProps: {
            src: IconCheckmark,
            size: 13,
            ...({ height: 6, width: 8 } as any),
        },
        css: css`
            &:not(:disabled) {
                cursor: pointer;
            }
            &&&[disabled] {
                cursor: not-allowed;
            }

            ${({ _labelPosition }: any) =>
                _labelPosition === "left" &&
                css`
                    margin-left: 0;
                `}

            ${defaultStateStyles}
            ${hoverStyle}
            ${checkedStyle}
            ${disabledStyle}
        `,
    },
    groupDefaultProps: {
        labelProps: {
            size: fontConfig.small.size,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.small.lineHeight};
                &:empty {
                    display: none;
                }
            `,
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle} {
                &:first-child {
                    margin-bottom: 12px;
                }
                &:empty {
                    display: none;
                }
            }
            ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
                margin-top: 12px;
            }
        `,
    },
} as xBaseTheme["checkbox"];
